import { useState } from "react";
import { Link } from "react-router-dom";
import { Download, Plus } from "lucide-react";
import { LienBouton, stylesBouton } from "@/composants/ui/bouton";
import { Carte } from "@/composants/ui/carte";
import { Champ } from "@/composants/ui/champ";
import { Chargement, Erreur, Vide } from "@/composants/ui/etats";
import { Etiquette } from "@/composants/ui/etiquette";
import { PiedDeListe } from "@/composants/ui/pied-de-liste";
import { useListePaginee } from "@/lib/chargement";
import { statutFournisseur, tonStatutFournisseur } from "@/lib/libelles";
import { api, type Fournisseur } from "@/lib/api";
import { useSession } from "@/session";

/**
 * Les fournisseurs (LOT 2) : un référentiel, que chacun consulte et que seul
 * qui passe les commandes fournisseurs enrichit. Le bouton de création est
 * masqué aux autres ; le serveur les refuse quoi qu'il en soit.
 *
 * L'export CSV (V5.0 §37) est réservé à qui tient « Exporter l'annuaire des
 * fournisseurs » (l'Administrateur, par défaut) ; il suit le filtre en cours, et
 * chaque export est au journal.
 */
export function PageFournisseurs() {
  const session = useSession();
  const peutCreer = session.peut("GERER_COMMANDE_FOURNISSEUR");
  const peutExporter = session.peut("EXPORTER_ANNUAIRE_FOURNISSEURS");
  const [terme, setTerme] = useState("");
  const [cherche, setCherche] = useState("");
  const liste = useListePaginee<Fournisseur>(
    (decalage, signal) => api.fournisseurs({ q: cherche || undefined, decalage }, signal),
    [cherche],
  );

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-encre">Fournisseurs</h1>
          <p className="mt-1 text-sm text-encre-douce">
            Ceux chez qui LAB commande, avec leurs contacts et leur délai moyen.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          {/* Un lien et non un bouton : le navigateur télécharge, avec la session.
              Masqué sans la permission ; le serveur refuse de toute façon. */}
          {peutExporter ? (
            <a
              href={api.adresseExportFournisseurs({ q: cherche || undefined })}
              download
              className={stylesBouton({ variante: "secondaire" })}
            >
              <Download className="h-4 w-4" aria-hidden />
              Exporter en CSV
            </a>
          ) : null}
          {peutCreer ? (
            <LienBouton to="/fournisseurs/nouveau" variante="principal">
              <Plus className="h-4 w-4" aria-hidden />
              Nouveau fournisseur
            </LienBouton>
          ) : null}
        </div>
      </div>

      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(evenement) => {
          evenement.preventDefault();
          setCherche(terme.trim());
        }}
      >
        <Champ
          libelle="Filtrer"
          className="min-w-[14rem] flex-1"
          placeholder="Nom, SIRET, ville…"
          value={terme}
          onChange={(evenement) => setTerme(evenement.target.value)}
        />
      </form>

      {liste.erreur ? <Erreur message={liste.erreur} onReessayer={liste.recharger} /> : null}
      {!liste.erreur && liste.enCours ? <Chargement message="Chargement des fournisseurs…" /> : null}

      {!liste.enCours && liste.total === 0 ? (
        <Carte>
          <Vide
            titre="Aucun fournisseur"
            detail={
              cherche
                ? "Aucun fournisseur ne correspond à cette recherche."
                : "Les fiches fournisseur apparaissent ici dès leur création."
            }
          />
        </Carte>
      ) : null}

      {liste.elements.length > 0 ? (
        <Carte>
          <ul className="divide-y divide-filet">
            {liste.elements.map((fournisseur) => (
              <li key={fournisseur.id}>
                <Link
                  to={`/fournisseurs/${fournisseur.id}`}
                  className="flex flex-col gap-1 px-4 py-3 hover:bg-chene-clair/25 sm:flex-row sm:items-center sm:gap-4"
                >
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-encre">
                    {fournisseur.nom}
                  </span>
                  <span className="text-sm text-encre-douce sm:w-40 sm:shrink-0">
                    {fournisseur.ville ?? "—"}
                  </span>
                  <span className="font-mono text-sm text-encre-douce sm:w-36 sm:shrink-0">
                    {fournisseur.siret ?? "Sans SIRET"}
                  </span>
                  <span className="sm:w-24 sm:shrink-0">
                    <Etiquette ton={tonStatutFournisseur(fournisseur.statut)}>
                      {statutFournisseur(fournisseur.statut)}
                    </Etiquette>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          <PiedDeListe
            nom="fournisseur"
            affiches={liste.elements.length}
            total={liste.total}
            reste={liste.reste}
            enCours={liste.chargeLaSuite}
            onCharger={liste.chargerLaSuite}
          />
        </Carte>
      ) : null}
    </div>
  );
}
